import React from 'react';
import styled from 'styled-components';
import { FaHeart, FaUsers, FaRocket, FaShieldAlt } from 'react-icons/fa';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  color: #ccc;
  font-size: 18px;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const SectionContent = styled.div`
  color: #ccc;
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 24px;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-top: 32px;
`;

const FeatureCard = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #333;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const FeatureIcon = styled.div`
  color: #4CAF50;
  font-size: 32px;
  margin-bottom: 16px;
`;

const FeatureTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const FeatureDescription = styled.p`
  color: #ccc;
  font-size: 14px;
  line-height: 1.6;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 24px;
  margin: 48px 0;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 24px;
  background-color: #111;
  border-radius: 12px;
  border: 1px solid #333;
`;

const StatNumber = styled.div`
  color: #4CAF50;
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  color: #ccc;
  font-size: 14px;
  font-weight: 500;
`;

const TeamSection = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #333;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const TeamMember = styled.div`
  text-align: center;
`;

const MemberAvatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin: 0 auto 16px;
  border: 3px solid #4CAF50;
`;

const MemberName = styled.div`
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const MemberRole = styled.div`
  color: #4CAF50;
  font-size: 14px;
  margin-bottom: 8px;
`;

const MemberBio = styled.div`
  color: #ccc;
  font-size: 12px;
  line-height: 1.5;
`;

const ContactSection = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #333;
  text-align: center;
`;

const ContactTitle = styled.h3`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const ContactInfo = styled.div`
  color: #ccc;
  font-size: 16px;
  line-height: 1.6;
`;

const AboutPage = () => {
  return (
    <Container>
      <Content>
        <Header>
          <Title>About Weloop</Title>
          <Subtitle>
            A modern social media platform designed to connect people, share moments, and build meaningful relationships in the digital age.
          </Subtitle>
        </Header>

        <Section>
          <SectionTitle>
            <FaRocket />
            Our Mission
          </SectionTitle>
          <SectionContent>
            At Weloop, we believe in the power of authentic connections. Our platform is built to foster genuine relationships, 
            meaningful conversations, and creative expression. We're committed to creating a safe, inclusive space where people 
            can share their stories, discover new perspectives, and build communities around shared interests.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>
            <FaHeart />
            What Makes Us Different
          </SectionTitle>
          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon>
                <FaUsers />
              </FeatureIcon>
              <FeatureTitle>Community First</FeatureTitle>
              <FeatureDescription>
                We prioritize building strong, supportive communities where every voice matters and connections flourish.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <FaShieldAlt />
              </FeatureIcon>
              <FeatureTitle>Privacy & Security</FeatureTitle>
              <FeatureDescription>
                Your privacy is our top priority. We use advanced security measures to protect your data and ensure a safe experience.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <FaRocket />
              </FeatureIcon>
              <FeatureTitle>Innovation</FeatureTitle>
              <FeatureDescription>
                We continuously innovate to provide the best user experience with cutting-edge features and intuitive design.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <FaHeart />
              </FeatureIcon>
              <FeatureTitle>Authentic Connections</FeatureTitle>
              <FeatureDescription>
                Focus on real relationships and meaningful interactions rather than just accumulating followers.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </Section>

        <StatsSection>
          <StatCard>
            <StatNumber>10K+</StatNumber>
            <StatLabel>Active Users</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>50K+</StatNumber>
            <StatLabel>Posts Shared</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>100K+</StatNumber>
            <StatLabel>Connections Made</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>99.9%</StatNumber>
            <StatLabel>Uptime</StatLabel>
          </StatCard>
        </StatsSection>

        <Section>
          <SectionTitle>
            <FaUsers />
            Our Team
          </SectionTitle>
          <TeamSection>
            <SectionContent>
              Meet the passionate team behind Weloop. We're a diverse group of developers, designers, and innovators 
              committed to building the future of social media.
            </SectionContent>
            <TeamGrid>
              <TeamMember>
                <MemberAvatar src="/default-avatar.png" alt="Team Member" />
                <MemberName>Alex Johnson</MemberName>
                <MemberRole>CEO & Founder</MemberRole>
                <MemberBio>
                  Passionate about creating meaningful digital experiences and building communities that matter.
                </MemberBio>
              </TeamMember>

              <TeamMember>
                <MemberAvatar src="/default-avatar.png" alt="Team Member" />
                <MemberName>Sarah Chen</MemberName>
                <MemberRole>CTO</MemberRole>
                <MemberBio>
                  Tech enthusiast with 10+ years of experience in building scalable social platforms.
                </MemberBio>
              </TeamMember>

              <TeamMember>
                <MemberAvatar src="/default-avatar.png" alt="Team Member" />
                <MemberName>Mike Rodriguez</MemberName>
                <MemberRole>Lead Designer</MemberRole>
                <MemberBio>
                  Creative designer focused on user experience and creating beautiful, intuitive interfaces.
                </MemberBio>
              </TeamMember>

              <TeamMember>
                <MemberAvatar src="/default-avatar.png" alt="Team Member" />
                <MemberName>Emily Davis</MemberName>
                <MemberRole>Community Manager</MemberRole>
                <MemberBio>
                  Dedicated to fostering positive communities and ensuring every user feels welcome and supported.
                </MemberBio>
              </TeamMember>
            </TeamGrid>
          </TeamSection>
        </Section>

        <Section>
          <ContactSection>
            <ContactTitle>Get in Touch</ContactTitle>
            <ContactInfo>
              Have questions, suggestions, or just want to say hello? We'd love to hear from you!<br />
              Email us at: <strong>hello@weloop.tech</strong><br />
              Follow us on social media for updates and behind-the-scenes content.
            </ContactInfo>
          </ContactSection>
        </Section>
      </Content>
    </Container>
  );
};

export default AboutPage; 